import { Alert, AlertTitle, Box, Button, Chip, Grid, TextField, Typography } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ElectronicInvoiceReceiver } from 'src/modules/invoice/models/electronicInvoiceReceiver';
import { CrediflowParty, getPartyCompliance } from 'src/modules/invoice/models/crediflowParty';
import { EInvoiceReceiverSearchFilter } from 'src/modules/invoice/models/eInvoiceReceiverSearchFilter';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import SearchField from 'src/shared/components/form/searchField';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { createElectronicInvoiceAddress, updateElectronicInvoiceAddress } from 'src/shared/services/electronicInvoiceAddressService';
import { getEInvoiceSender } from 'src/shared/services/electronicInvoiceSenderService';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import {
  createElectronicInvoiceReceiver,
  getAllElectronicInvoiceReceivers,
  inviteElectronicInvoiceReceiver,
  lookupElectronicInvoiceReceiver,
} from 'src/shared/services/electronicInvoiceReceiverService';
import { Member } from '../models/member';

type EInvoiceSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  member: Member;
  refetchMemberCallback: () => void;
  /** Pre-fill CIN search field and go directly to "add new" view */
  initialCinSearch?: string;
  /** Pre-fill GLN search field */
  initialGlnSearch?: string;
  /** If set, update existing address instead of creating a new one */
  existingAddressId?: number;
  /** Pre-fill buyerReference field */
  initialBuyerReference?: string;
};

export const EInvoiceSettingsModal = (props: EInvoiceSettingsModalProps) => {
  const { open, onClose, member, refetchMemberCallback, initialCinSearch, initialGlnSearch, existingAddressId, initialBuyerReference } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const [hasAnySender, setHasAnySender] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open) return;
    getAllInvoiceGroups(0, 500, 'name', 'asc', '')
      .then((res) => {
        const groups = res.data.results;
        if (groups.length === 0) {
          if (isMounted()) setHasAnySender(false);
          return Promise.resolve();
        }
        return Promise.all(
          groups.map((g) =>
            getEInvoiceSender(g.owner_id)
              .then((senderRes) => !!senderRes.data)
              .catch(() => false),
          ),
        ).then((results) => {
          if (isMounted()) setHasAnySender(results.some((r) => r));
        });
      })
      .catch(() => {
        if (isMounted()) setHasAnySender(false);
      });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedReceiver, setSelectedReceiver] = useState<ElectronicInvoiceReceiver | undefined>(undefined);
  const [showAddNew, setShowAddNew] = useState<boolean>(false);
  const [ownerId, setOwnerId] = useState<number | undefined>(undefined);
  const [cinSearch, setCinSearch] = useState<string>(initialCinSearch || '');
  const [glnSearch, setGlnSearch] = useState<string>(initialGlnSearch || '');
  const [cinSearching, setCinSearching] = useState<boolean>(false);
  const [cinSearchResult, setCinSearchResult] = useState<CrediflowParty[] | undefined>(undefined);

  // Confirm/link flow state
  const [selectedParty, setSelectedParty] = useState<CrediflowParty | undefined>(undefined);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [accountingCost, setAccountingCost] = useState<string>('');
  const [buyerReference, setBuyerReference] = useState<string>(initialBuyerReference || '');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [rows, setRows] = useState<ElectronicInvoiceReceiver[]>([]);
  const [searchFilter, setSearchFilter] = useState<EInvoiceReceiverSearchFilter>({
    sortCol: 'name',
    sortDir: 'asc',
    pageIndex: 0,
    pageSize: 10,
    filter: initialCinSearch || '',
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 15, 25, 50],
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k: string, v: unknown) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, page: searchFilter.pageIndex, pageSize: searchFilter.pageSize }));
  }, [searchFilter.pageIndex, searchFilter.pageSize]);

  useEffect(() => {
    const fetchData = async () => {
      if (isMounted()) updateData('loading', true);
      try {
        const result = await getAllElectronicInvoiceReceivers(searchFilter);
        if (isMounted()) {
          updateData('totalRows', result.data.totalItems);
          setTimeout(() => {
            setRows(result.data.results.map((item) => ({ ...item })));
          }, 100);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) updateData('loading', false);
      }
    };
    fetchData();
  }, [searchFilter, isMounted]);

  const onPageChange = (newPageIndex: number) => {
    setSearchFilter((prev) => ({ ...prev, pageIndex: newPageIndex }));
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    setSearchFilter((prev) => ({ ...prev, pageIndex: 0, pageSize: newPageSize }));
    scrollIntoView();
  };

  const onRowClick = (params: { row: ElectronicInvoiceReceiver }) => {
    setSelectedReceiver(params.row);
    setAccountingCost('');
    setBuyerReference(initialBuyerReference || '');
  };

  const handleLinkReceiver = async (receiverId: number) => {
    if (!member.member_id) return;
    setIsSaving(true);
    try {
      if (existingAddressId) {
        await updateElectronicInvoiceAddress({
          eInvoiceAddress_id: existingAddressId,
          member_id: member.member_id,
          eInvoiceReceiver_id: receiverId,
          accountingCost,
          buyerReference,
        });
      } else {
        await createElectronicInvoiceAddress({
          member_id: member.member_id,
          eInvoiceReceiver_id: receiverId,
          accountingCost,
          buyerReference,
        });
      }
      if (isMounted()) {
        toast.success(<>{t('member:eInvoiceSettings.linkSuccess')}</>);
        refetchMemberCallback();
        onClose();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('member:eInvoiceSettings.linkError')}</>);
      }
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const handleSaveAndLinkParty = async (party: CrediflowParty) => {
    if (!member.member_id) return;
    setIsSaving(true);
    try {
      const receiverData: ElectronicInvoiceReceiver = {
        id: 0,
        party_id: party.id,
        cin: party.cin,
        gln: party.gln,
        name: party.name,
        street1: party.address1,
        street2: party.address2,
        city: party.city,
        zip: party.zipCode,
        countryCode: party.countryCode || 'SE',
        compliance: getPartyCompliance(party) || 'prefer',
      };
      const createResult = await createElectronicInvoiceReceiver(receiverData);
      const newReceiverId = typeof createResult.data === 'number' ? createResult.data : (createResult.data as ElectronicInvoiceReceiver).id;
      if (existingAddressId) {
        await updateElectronicInvoiceAddress({
          eInvoiceAddress_id: existingAddressId,
          member_id: member.member_id,
          eInvoiceReceiver_id: newReceiverId,
          accountingCost,
          buyerReference,
        });
      } else {
        await createElectronicInvoiceAddress({
          member_id: member.member_id,
          eInvoiceReceiver_id: newReceiverId,
          accountingCost,
          buyerReference,
        });
      }
      if (isMounted()) {
        toast.success(<>{t('member:eInvoiceSettings.linkSuccess')}</>);
        refetchMemberCallback();
        onClose();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('member:eInvoiceSettings.linkError')}</>);
      }
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!selectedParty || !ownerId || !inviteEmail) return;
    setIsSaving(true);
    try {
      await inviteElectronicInvoiceReceiver(ownerId, selectedParty.id, inviteEmail);
      if (isMounted()) {
        toast.success(<>{t('member:eInvoiceSettings.inviteSent')}</>);
        setSelectedParty(undefined);
        setInviteEmail('');
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('member:eInvoiceSettings.inviteError')}</>);
      }
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const handleSearch = async (searchCin?: string, searchGln?: string) => {
    if (!ownerId) return;
    if (!searchCin && !searchGln) return;
    if (searchCin && searchCin.length < 2) return;
    if (searchGln && searchGln.length < 2) return;
    setCinSearching(true);
    setCinSearchResult(undefined);
    setSelectedParty(undefined);
    try {
      const result = await lookupElectronicInvoiceReceiver(ownerId, searchCin || undefined, searchGln || undefined);
      if (isMounted()) {
        setCinSearchResult(result.data);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        setCinSearchResult([]);
      }
    } finally {
      if (isMounted()) setCinSearching(false);
    }
  };

  const getComplianceLabel = (party: CrediflowParty): string => {
    const compliance = getPartyCompliance(party);
    if (compliance === 'prefer') return t('member:eInvoiceSettings.compliancePrefer');
    if (compliance === 'permits') return t('member:eInvoiceSettings.compliancePermits');
    return '';
  };

  const getComplianceColor = (party: CrediflowParty): 'success' | 'warning' | 'default' => {
    const compliance = getPartyCompliance(party);
    if (compliance === 'prefer') return 'success';
    if (compliance === 'permits') return 'warning';
    return 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('member:eInvoiceSettings.receiverName'),
      sortable: true,
      flex: 3,
    },
    {
      field: 'cin',
      headerName: t('member:eInvoiceTooltip.cin'),
      sortable: true,
      flex: 2,
    },
    {
      field: 'gln',
      headerName: 'GLN',
      sortable: true,
      flex: 2,
    },
    {
      field: 'city',
      headerName: t('member:eInvoiceSettings.city'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => <>{params.row.city}</>,
    },
  ];

  const renderConfirmLinkFields = (receiverName: string, onConfirm: () => void, onCancel: () => void) => (
    <Box sx={{ p: 2, mt: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
      <Typography variant='body2' fontWeight={600} sx={{ mb: 2 }}>
        {t('member:eInvoiceSettings.confirmLinkDescription')} {receiverName}?
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size='small'
            label={t('member:eInvoiceSettings.accountingCost')}
            value={accountingCost}
            onChange={(e) => setAccountingCost(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size='small'
            label={t('member:eInvoiceSettings.buyerReference')}
            value={buyerReference}
            onChange={(e) => setBuyerReference(e.target.value)}
          />
        </Grid>
      </Grid>
      <Box mt={2} display='flex' gap={1} justifyContent='flex-end'>
        <Button variant='text' size='small' onClick={onCancel} disabled={isSaving}>
          {t('common:button.cancel')}
        </Button>
        <Button variant='contained' size='small' onClick={onConfirm} disabled={isSaving}>
          {t('member:eInvoiceSettings.confirmLink')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <GenericDialog
      dialogTitle={t('member:addEInvoice')}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSave={() => {}}
      onClose={onClose}
      open={open}
      maxWidth='md'
      showDialogActions={false}
      dialogContent={
        <>
          {hasAnySender === false && (
            <Alert severity='info'>
              <AlertTitle>{t('invoice:eInvoice.noSenderWarningTitle')}</AlertTitle>
              {t('invoice:eInvoice.noSenderWarningText')}
            </Alert>
          )}
          {hasAnySender !== false && !showAddNew && (
            <Box ref={scrollRef}>
              <Typography variant='body2' sx={{ mb: 1 }}>
                {t('member:eInvoiceSettings.selectReceiver')}
              </Typography>
              <PageFilterSection
                leftContent={
                  <SearchField
                    initialValue={searchFilter.filter || ''}
                    onSearch={(newSearchString) => {
                      setSearchFilter((prev) => ({ ...prev, filter: newSearchString, pageIndex: 0 }));
                    }}
                  />
                }
              />
              <DataGrid<ElectronicInvoiceReceiver>
                columns={columns}
                rows={rows}
                autoHeight
                dataModel={data}
                checkboxSelection={false}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => {
                  const newSort = newSortModel[0]?.sort === 'asc' ? 'desc' : 'asc';
                  setSortModel(newSortModel);
                  setSearchFilter((prev) => ({
                    ...prev,
                    pageIndex: 0,
                    sortCol: newSortModel[0]?.field,
                    sortDir: newSort,
                  }));
                }}
                onRowClick={onRowClick}
                selectionModel={[]}
                onSelectionModelChange={() => ConsoleHelper.log('')}
              />

              {selectedReceiver &&
                renderConfirmLinkFields(
                  selectedReceiver.name,
                  () => handleLinkReceiver(selectedReceiver.id),
                  () => setSelectedReceiver(undefined),
                )}

              <Box mt={2} display='flex' justifyContent='flex-end'>
                <Button variant='outlined' size='small' onClick={() => setShowAddNew(true)}>
                  {t('member:eInvoiceSettings.addNewReceiver')}
                </Button>
              </Box>
            </Box>
          )}
          {hasAnySender !== false && showAddNew && (
            <Box>
              <Typography variant='body2' sx={{ mb: 2 }}>
                {t('member:eInvoiceSettings.searchByCinOrGln')}
              </Typography>
              <Grid container spacing={1} alignItems='center'>
                <Grid item xs={12} sx={{ mb: 1 }}>
                  <InvoiceGroups
                    required
                    ownerId={ownerId}
                    autoSelect
                    emptyLabel={t('common:button.choose')}
                    placeholder={t('common:button.choose')}
                    callback={(ownId) => {
                      setOwnerId(ownId === 0 ? undefined : ownId);
                      setCinSearchResult(undefined);
                      setSelectedParty(undefined);
                    }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    size='small'
                    label={t('member:eInvoiceTooltip.cin')}
                    value={cinSearch}
                    onChange={(e) => {
                      setCinSearch(e.target.value);
                      if (e.target.value) setGlnSearch('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch(cinSearch);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    size='small'
                    label='GLN'
                    value={glnSearch}
                    onChange={(e) => {
                      setGlnSearch(e.target.value);
                      if (e.target.value) setCinSearch('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch(undefined, glnSearch);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant='contained'
                    size='small'
                    onClick={() => handleSearch(cinSearch || undefined, glnSearch || undefined)}
                    disabled={cinSearching || (!cinSearch && !glnSearch) || !ownerId}
                  >
                    {t('common:button.search')}
                  </Button>
                </Grid>
              </Grid>

              {cinSearchResult !== undefined && (
                <Box mt={2}>
                  {cinSearchResult.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                      {t('member:eInvoiceSettings.noResults')}
                    </Typography>
                  ) : (
                    cinSearchResult.map((party) => {
                      const compliance = getPartyCompliance(party);
                      const isSelected = selectedParty?.id === party.id;

                      return (
                        <Box
                          key={party.id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            border: '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'action.selected' : 'inherit',
                            '&:hover': { backgroundColor: isSelected ? 'action.selected' : 'action.hover' },
                          }}
                          onClick={() => {
                            setSelectedParty(isSelected ? undefined : party);
                            setInviteEmail(party.email || '');
                            setAccountingCost('');
                            setBuyerReference(initialBuyerReference || '');
                          }}
                        >
                          <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography variant='body2' fontWeight={600}>
                              {party.name}
                            </Typography>
                            {compliance && <Chip label={getComplianceLabel(party)} color={getComplianceColor(party)} size='small' />}
                          </Box>
                          {party.cin && (
                            <Typography variant='body2' color='text.secondary'>
                              {t('member:eInvoiceTooltip.cin')}: {party.cin}
                            </Typography>
                          )}
                          {party.gln && (
                            <Typography variant='body2' color='text.secondary'>
                              GLN: {party.gln}
                            </Typography>
                          )}
                          {party.city && (
                            <Typography variant='body2' color='text.secondary'>
                              {party.city}
                            </Typography>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              )}

              {selectedParty &&
                getPartyCompliance(selectedParty) === 'prefer' &&
                renderConfirmLinkFields(
                  selectedParty.name,
                  () => handleSaveAndLinkParty(selectedParty),
                  () => setSelectedParty(undefined),
                )}

              {selectedParty && getPartyCompliance(selectedParty) === 'permits' && (
                <Box mt={2} sx={{ p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                  <Typography variant='body2' sx={{ mb: 1 }}>
                    {t('member:eInvoiceSettings.inviteDescription')}
                  </Typography>
                  <Grid container spacing={1} alignItems='center'>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        size='small'
                        label={t('member:eInvoiceSettings.inviteEmail')}
                        type='email'
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSendInvite();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Button variant='contained' size='small' onClick={handleSendInvite} disabled={isSaving || !inviteEmail}>
                        {t('member:eInvoiceSettings.sendInvite')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box mt={2}>
                <Button variant='text' size='small' onClick={() => setShowAddNew(false)}>
                  {t('common:button.back')}
                </Button>
              </Box>
            </Box>
          )}
        </>
      }
    />
  );
};
